<script setup lang="ts">
  import type { ComputedRef } from 'vue'
  import type {
    Maybe,
    RefComponent,
    Setting,
    SettingMarketingPopupValue,
  } from '@/types'
  import type { CustomerMarketingPopup } from '@/types/ui'
  import type { CustomerPopupSubscription } from '@/types/customer/subscriptions'

  import { computed, nextTick, onMounted, ref, watch } from 'vue'

  import { isEmpty, validateEmail } from '@/helpers'

  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui'
  import { useCustomerSettingsStore } from '@/stores/customer/settings'
  import { useCustomerAuthStore } from '@/stores/customer/auth'
  import { useCustomerSubscriptionsStore } from '@/stores/customer/subscriptions'

  import Model from '@/models/Model'
  import PopupSubscriptionModel from '@/models/PopupSubscriptionModel'

  import CustomerModal2 from '@/components/Modals/Customer/CustomerModal2.vue'

  import ManagerInput from '@/components/Global/Manager/Forms/ManagerInput.vue'
  import ManagerButton from '@/components/Global/Manager/Forms/ManagerButton.vue'

  import imageUrl from '@/assets/customer/images/popup.png'
import router from '@/router'
  /**
   * Form
   * */
  const customerAuthStore = useCustomerAuthStore()
  const { currentUser } = storeToRefs(customerAuthStore)

  const email = ref<Maybe<string>>(null)
  const emailInputRef: RefComponent<typeof ManagerInput> = ref(null)

  watch(
    () => currentUser.value?.email,
    () => {
      if (!currentUser.value?.email) return

      email.value = currentUser.value.email
    },
    { immediate: true }
  )

  const customerSubscriptionsStore = useCustomerSubscriptionsStore()
  const { singleItemIsLoading: subscribing } = storeToRefs(
    customerSubscriptionsStore
  )

  const remoteError = ref<string>('')
  const remoteValidation = ref<Record<string, string[]>>({})

  async function submit() {
    remoteValidation.value = {}
    remoteError.value = ''

    if (!(await emailInputRef.value?.validate()) || subscribing.value) return

    const { error, validationErrors, otherError } =
      await customerSubscriptionsStore.subscribe<CustomerPopupSubscription>(
        PopupSubscriptionModel.mergeTogether({
          email: email.value,
        })
      )

    if (!error) {
      subscribed.value = true

      if (counter.value === 0 && canShowPopupAgain.value) {
        lastPopupTimestamp.value = new Date().getTime()
        await nextTick()
      }

      close()
    } else if (!isEmpty(validationErrors) || otherError) {
      resetErrors(validationErrors, otherError)
    }
  }

  async function reset() {
    email.value = currentUser.value?.email ?? null

    timer.value = popupSettings.value?.value.show_timeout ?? null

    startTimer()

    await nextTick()

    emailInputRef.value?.resetLazyValidation()
    resetErrors()
  }

  function resetErrors(validation = {}, other = '') {
    remoteValidation.value = validation
    remoteError.value = other
  }
  /**
   * Modal
   * */
  const customerModalRef: RefComponent<typeof CustomerModal> = ref(null)

  const ui = useUiStore()
  const { popupBannerVisibility2, lastPopupTimestamp, subscribed } =
    storeToRefs(ui)

  const canShowPopupAgain = computed(() => {
    if (currentUser.value?.subscribed || subscribed.value) return false

    const lastMidnightTimestamp = new Date().setHours(0, 0, 0, 0)

    return (
      !lastPopupTimestamp.value ||
      (lastPopupTimestamp.value &&
        lastPopupTimestamp.value < lastMidnightTimestamp)
    )
  })

  watch(
    () => popupBannerVisibility2.value,
    () => {
      if (popupBannerVisibility2.value) show()
      else close()
    }
  )

  function show() {
    customerModalRef.value?.show()
  }

  function close() {
    if (customerModalRef.value?.active) {
      customerModalRef.value.close()
    }

    if (popupBannerVisibility2.value) {
      popupBannerVisibility2.value = false
    }

    const timeoutId = setTimeout(() => {
      reset()

      clearTimeout(timeoutId)
    }, 300)
  }
  /**
   * Popup settings
   * */
  const settingsStore = useCustomerSettingsStore()
  const { isLoading } = storeToRefs(settingsStore)

  const popupSettings = computed(() =>
    settingsStore.getItemByName('popup')
  ) as ComputedRef<Maybe<Setting<SettingMarketingPopupValue>>>

  async function checkSettings() {
    if (!popupSettings.value) {
      await settingsStore.fetchItemByName('popup')
      await nextTick()
    }

    return popupSettings.value?.value.show
  }

  onMounted(async () => {
    if (!(await checkSettings())) return

    initPopup()
  })
  /**
   * Timer & counter
   * */
  const timerTimestamp = ref<Maybe<number>>(null)
  const timer = ref<Maybe<number>>(null)
  const intervalId = ref<Maybe<number>>(null)

  const counter = ref<Maybe<number>>(null)

  function startTimer() {
    if (
      !(counter.value && intervalId.value === null && canShowPopupAgain.value)
    ) {
      return
    }

    if (
      popupSettings.value &&
      timer.value === popupSettings.value.value.show_timeout
    ) {
      timerTimestamp.value = new Date().getTime()
    } else {
      const state = ui.getFromSessionStorage<CustomerMarketingPopup>('popup')

      timerTimestamp.value = state?.timerTimestamp ?? null
    }

    intervalId.value = setInterval(tick, 1000, { intervalId })
  }

  function tick() {
    if (timer.value === null) return

    timer.value--

    if (timer.value !== 0) return

    const timeoutId = setTimeout(() => {
      clearTimer()

      clearTimeout(timeoutId)
    }, 100)
  }

  function clearTimer() {
    if (intervalId.value !== null) {
      clearInterval(intervalId.value)

      intervalId.value = null
    }
  }

  watch(
    () => timer.value,
    () => {
      if (timer.value !== 0 || !counter.value || intervalId.value === null) {
        return
      }

      show()

      counter.value--
      timerTimestamp.value = null
    },
    { immediate: true }
  )
  /**
   * Запускается сразу после initPopup
   * */
  watch(
    () => [counter.value, timerTimestamp.value],
    () => {
      const state = ui.getFromSessionStorage<CustomerMarketingPopup>('popup')

      if (!popupBannerVisibility2.value) startTimer()

      if (!state) {
        savePopupState()
      } else if (
        !isEmpty(
          Model.difference(state, {
            settings: popupSettings.value?.value ?? state.settings,
            timerTimestamp: timerTimestamp.value,
            counter: counter.value,
          })
        )
      ) {
        if (counter.value === 0 && canShowPopupAgain.value) {
          lastPopupTimestamp.value = new Date().getTime()
        }

        savePopupState()
      }
    }
  )

  function initPopup() {
    if (!popupSettings.value || !canShowPopupAgain.value) return

    const state = ui.getFromSessionStorage<CustomerMarketingPopup>('popup')

    timer.value = calcTimer(state, popupSettings.value.value.show_timeout)
    /**
     * В первый раз watcher реагирует на counter
     * */
    counter.value = state?.counter ?? popupSettings.value.value.show_repeat_max
  }

  function savePopupState() {
    if (!popupSettings.value) return

    const state: CustomerMarketingPopup = {
      settings: popupSettings.value.value,
      timerTimestamp: timerTimestamp.value,
      counter: counter.value,
    }

    ui.saveToSessionStorage('popup', state)
  }

  function calcTimer(
    state: Maybe<CustomerMarketingPopup>,
    showTimeout: number
  ) {
    const start = state?.timerTimestamp
    if (!start) return showTimeout

    const now = new Date().getTime()

    const delta = now - start
    if (delta <= 0) return 1

    const seconds = Math.floor(delta / 1000)
    if (seconds >= showTimeout) return 1

    return (showTimeout ?? seconds + 1) - seconds
  }

  function handleClick() {
    close()
    router.push('/catalogs/sunglasses');
  }
</script>

<template>
  <CustomerModal2
    ref="customerModalRef"
    image-alt="Подпишитесь на рассылку"
    image-classes="w-auto max-w-[335px] h-[335px] md:w-[450px] md:h-[450px] md:max-w-[49.23%]"
    body-classes="flex flex-col flex-1 justify-center items-center"
    modal-wrapper-classes="w-full h-[580px] md:h-[450px] flex-col md:flex-row max-w-[335px] md:max-w-[900px]"
    modal-classes="flex flex-col flex-1 justify-center w-full md:pt-[54px] pb-[34px] md:pb-[70px] md:min-h-[450px]"
    mobile-height="auto"
    @close="close"
  >
    <template #body>
      <div class="flex flex-1 justify-center items-center">
        <h2
          class="text-[#000] font-bold uppercase text-center leading-[1.2] text-[27px] md:text-[32px]"
        >
          Доставим<br />5&nbsp;очков на&nbsp;выбор<br />с&nbsp;примеркой
        </h2>
      </div>
      <router-link
  to="/catalogs/sunglasses"
  class="btn-shutter"
  aria-label="Перейти в каталог для выбора очков"
  @click.prevent="handleClick"
>
<span>Подробнее</span>
  
</router-link>
    </template>
  </CustomerModal2>
</template>


<style scoped>
.btn-shutter {
  @apply relative overflow-hidden flex items-center justify-center w-full max-w-[270px] p-[14px] text-[18px] leading-[1.2] uppercase font-normal border border-black text-black transition-colors duration-300 active:hover:bg-black active:hover:text-white md:hover:bg-black md:hover:text-white;
}
</style>
